import { useState } from 'react';
import { apiJson } from '../api/client';
import { mutateGallery } from '../hooks/galleryCache';
import { NewAssetModal } from './NewAssetModal';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const ENDPOINT = '/api/tools/music';

interface CreateResult {
  id: string;
  [k: string]: unknown;
}

export function NewMusicDialog({ open, onClose, onCreated }: Props) {
  const [prompt, setPrompt] = useState('');
  const [durationMode, setDurationMode] = useState<'clip' | 'full'>('clip');
  const [instrumental, setInstrumental] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const p = prompt.trim();
    if (!p) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await apiJson<CreateResult>('POST', ENDPOINT, {
        prompt: p,
        durationMode,
        instrumental,
      });
      mutateGallery(ENDPOINT, (prev) => {
        if (prev.some((it) => it.id === res.id)) return prev;
        return [res as unknown as { id: string }, ...prev];
      });
      onCreated?.();
      setPrompt('');
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button type="button" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={() => void submit()}
        disabled={busy || prompt.trim().length === 0}
      >
        {busy ? 'Generating…' : 'Generate'}
      </button>
    </>
  );

  return (
    <NewAssetModal
      open={open}
      onClose={onClose}
      busy={busy}
      title="New music track"
      footer={footer}
    >
      <div className="stack">
        <label className="stack">
          <span className="muted small">Prompt</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Dreamy slow synth pad with light percussion…"
          />
        </label>
        <div className="row">
          <label className="stack" style={{ flex: 1 }}>
            <span className="muted small">Duration</span>
            <select
              value={durationMode}
              onChange={(e) =>
                setDurationMode(e.target.value as 'clip' | 'full')
              }
            >
              <option value="clip">Clip (~30 s)</option>
              <option value="full">Full track</option>
            </select>
          </label>
          <label
            className="voice-toggle-row"
            style={{ alignSelf: 'flex-end' }}
          >
            <input
              type="checkbox"
              checked={instrumental}
              onChange={(e) => setInstrumental(e.target.checked)}
            />
            <span>Instrumental (no vocals)</span>
          </label>
        </div>
        {err ? <div className="status-missing small">{err}</div> : null}
      </div>
    </NewAssetModal>
  );
}
